import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

/**
 * Props:
 *  - label: string
 *  - value: string (HTML)
 *  - onChange: (html: string) => void
 *  - placeholder?: string
 *  - error?: string
 *  - className?: string
 */
export default function RteField({ label, value, onChange, placeholder, error, className }) {
  // Keep a local mirror to avoid parent <-> child thrash
  const [inner, setInner] = useState(value || "");
  const lastSentRef = useRef(value || "");
  const timerRef = useRef(null);

  // sanitize to avoid loops caused by invisible chars / trivial diffs
  const sanitize = useCallback(
    (html) => (html || "").replace(/\u200B/g, "").trim(),
    []
  );

  // Sync local state when parent value changes (only if actually different)
  useEffect(() => {
    const next = sanitize(value || "");
    const curr = sanitize(inner);
    if (next !== curr) setInner(next);
  }, [value]);

  // Stable toolbar/modules to avoid re-instantiation each render
  const modules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike", "clean", "code"],
        [{ header: [1, 2, 3, 4, false] }],
        ["blockquote", "link"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ align: [] }],
        ["hr"],
        ["undo", "redo"], // not native in quill; ignored if not provided by plugins
      ],
    }),
    []
  );

  const formats = useMemo(
    () => [
      "bold", "italic", "underline", "strike", "clean", "code",
      "header",
      "blockquote", "link",
      "list", "bullet",
      "script",
      "align",
      "hr",
    ],
    []
  );

  // Debounced change handler: update local, then notify parent only if changed
  const handleEditorChange = useCallback(
    (html) => {
      const next = sanitize(html);

      if (next !== inner) setInner(next);

      if (next !== lastSentRef.current) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          lastSentRef.current = next;
          onChange && onChange(next);
        }, 120);
      }
    },
    [inner, onChange, sanitize]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className={className}>
      {label && <label className="block mb-2 font-medium">{label}</label>}

      <div className="rounded-md border overflow-hidden rte-yellow-focus">
        <ReactQuill
          theme="snow"
          value={inner}
          onChange={handleEditorChange}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          style={{ minHeight: 160 }}
        />
      </div>

      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}

      {/* Yellow focus ring on click/focus */}
      <style>{`
        .rte-yellow-focus:focus-within {
          outline: 2px solid #FFCC00;
          outline-offset: 0;
          box-shadow: 0 0 0 3px rgba(255, 204, 0, 0.35);
          border-color: #FFCC00 !important;
          border-radius: 0.375rem;
        }
        /* Make the editor area feel like your inputs */
        .rte-yellow-focus .ql-container {
          border: none !important;
        }
        .rte-yellow-focus .ql-toolbar {
          border-bottom: 1px solid #e5e7eb !important;
        }
        .rte-yellow-focus .ql-editor {
          min-height: 120px;
        }
      `}</style>
    </div>
  );
}
